import { createEnhance } from '@halo/zustand-enhance';
import { setAutoFreeze } from 'immer';
import type { ClientStore, CreateClientOptions, InitClientOptions } from './client';
import { clientStoreCreator } from './client';
import type { UserStore } from './user';
import { userStoreCreator } from './user';
import type { CreateSessionsOptions, SessionsStore } from './session/sessions';
import { sessionsStoreCreator } from './session/sessions';
import { mediasStoreCreator, type CreateMediasOptions, type MediasStore } from './media';
import type { InitMediasOptions } from './media/medias';
import type { InitUserOptions } from './user';
import type { WindowStore } from './window';
import { windowStoreCreator } from './window/window';
import { storeLogger } from './logger';

setAutoFreeze(false);

export type CreateAppStoreOptions = CreateClientOptions &
  CreateSessionsOptions &
  CreateMediasOptions;

export type InitAppOptions = InitClientOptions & InitMediasOptions & InitUserOptions;

export interface MainStore {
  effects: Array<() => void> & Record<string, () => void>;
  createApp: (options: CreateAppStoreOptions) => Promise<boolean>;
  initApp: (options: InitAppOptions) => Promise<boolean>;
  [Symbol.dispose]: () => void;
}

export type AppStore = ClientStore &
  UserStore &
  SessionsStore &
  MediasStore &
  MainStore &
  WindowStore;

export const useAppStore = createEnhance<AppStore>((...args) => ({
  ...windowStoreCreator(...args),
  ...clientStoreCreator(...args),
  ...userStoreCreator(...args),
  ...sessionsStoreCreator(...args),
  ...mediasStoreCreator(...args),
  effects: [] as any,
  createApp: async (options) => {
    storeLogger.init(Boolean(options.debugLevel));
    const [, get] = args;
    const { createClient, createSessions, createMedias, winPromise } = get();

    const disabled = await winPromise;

    if (disabled) {
      return false;
    }

    createClient(options);
    createSessions(options);
    createMedias(options);

    return true;
  },
  initApp: async (options) => {
    const [set, get] = args;
    const { initClient, initUser, initSessions, initMedias, winPromise } = get();

    const disabled = await winPromise;

    if (disabled) {
      return false;
    }

    await initClient(options);
    await Promise.all([initUser(options), initSessions()]);
    initMedias();
    set({ initTrigger: {} });

    return true;
  },
  [Symbol.dispose]: () => {
    const [, get] = args;
    const { effects, disposeClient, disposeMedias } = get();
    Object.values(effects).forEach((remove) => {
      remove();
    });
    disposeClient();
    disposeMedias();
  },
}));

useAppStore.subscribe((store) => {
  storeLogger.log('appStore', store);
});
